/// Given a list of items and a groupSize
/// returns the items grouped by an amount of groupSize
/// Example: groupList( ['a', 'b', 'c'], 2 );

// eslint-disable-next-line import/no-extraneous-dependencies
import { camelCase, isArray, isObject,transform } from 'lodash';
import moment from 'moment';
import { read, utils } from 'xlsx';

import { DateUtil } from '@src/packages/utils/dates';
import { translateString } from '@src/util/intl/translateString';

export const isLocalhost = window.location.hostname === 'localhost';

/// returns [ ['a', 'b'], ['c'] ]
const WHERE_IN_FILTER_LIMIT = 10;
export const groupList = (items, groupSize = WHERE_IN_FILTER_LIMIT) => {
  const groups = [];

  for (let i = 0; i < items.length; i += groupSize) {
    const end = i + groupSize > items.length ? items.length : i + groupSize;
    const sublist = items.slice(i, end);
    groups.push(sublist);
  }

  return groups;
};

export const getFullUrl = (nextUrl, currentUrl) => {
  // Examples - Current url
  // 1- "someOrgId/-/eventTypes/event_type/edit/someEventTypeId/someDivId"
  // OR 2- "someOrgId/-"

  // Next Url is "eventTypes"
  // returns "someOrgId/-/eventTypes"
  const divider = '/-';
  const root = currentUrl.split(divider)[0];
  const newURL = `${root}${divider}/${nextUrl}`;
  return newURL;
};

export const convertMinutesToHoursAndMinutes = minutes => {
  const hours = Math.floor(minutes / 60);
  let remainingMinutes = minutes % 60;
  remainingMinutes = remainingMinutes === 0 ? '00' : remainingMinutes;

  return [String(hours), String(remainingMinutes)];
};

export const getInitialsName = name =>
  name &&
  name
    .split(' ')
    .map(item => item.slice(0, 1))
    .join('');

export const sortByField = (data, field) =>
  (data || []).sort((a, b) => {
    const aMs = DateUtil.getTimestampMs(a?.[field]) ?? 0;
    const bMs = DateUtil.getTimestampMs(b?.[field]) ?? 0;
    return bMs - aMs;
  });
// NOTE: Generate key was created to create unique ids and to do ref by each element html
export const generateKey = preData => `${preData}_${new Date().getTime()}`;

export const parserFile = (file, sheetIndex = 0) =>
  new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      const rABS = !!reader.readAsBinaryString;
      reader.onload = e => {
        const fileObj = e.target.result;
        const wb = read(fileObj, { type: rABS ? 'binary' : 'array' });
        const wsname = wb.SheetNames[sheetIndex];
        const ws = wb.Sheets[wsname];
        const data = utils.sheet_to_json(ws, { header: 1 });
        resolve(data);
      };
      if (rABS) reader.readAsBinaryString(file);
      else reader.readAsArrayBuffer(file);
    } catch (error) {
      reject(error);
    }
  });
// Convert to hexadecimal
function componentToHex(c) {
  const hex = c.toString(16);
  return hex.length === 1 ? `0${hex}` : hex;
}
export const rgbToHex = ({ r, g, b }) =>
  `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;

// Hex to RGB Object
export const hexToRgbaObject = (hex, alpha = 1) => {
  const output = hex.replace('#', '');

  const r = parseInt(output.substring(0, 2), 16);
  const g = parseInt(output.substring(2, 4), 16);
  const b = parseInt(output.substring(4, 6), 16);

  const rgbaObject = {
    r: r.toString(),
    g: g.toString(),
    b: b.toString(),
    a: alpha.toString(),
  };

  return rgbaObject;
};

export const deleteDuplicated = array =>
  array.filter((element, index) => array.indexOf(element) === index);

export const generateDelay = async time => new Promise(resolve => setTimeout(resolve, time));

export const getRandomColor = () => {
  // generate a random number between 0 and 16777215 (inclusive)
  const randomHex = Math.floor(Math.random() * 16777215);
  // convert the number to a hexadecimal string and pad with leading zeros
  return `#${randomHex.toString(16).padStart(6, '0')}`;
};

export const getUserTimeZoneDate = (millisDate, fromTimeZone, toTimeZone) => {
  const targetDate = moment.tz(millisDate, fromTimeZone).clone().tz(toTimeZone);
  const formatDate = targetDate.format('YYYY-MM-DD HH:mm:ss');

  return {
    format: formatDate,
    // Parse with an ISO `T` separator: `new Date('YYYY-MM-DD HH:mm:ss')` (space) is
    // Invalid Date in Safari/Firefox. We keep the wall-clock as a local Date on purpose.
    jsDate: new Date(targetDate.format('YYYY-MM-DDTHH:mm:ss')),
    momentDate: moment(formatDate),
  };
};

// Functio to convert moment date to minutes
export const convertDateToMinutes = date => {
  const currentTime = date.valueOf();
  return Math.round((currentTime - moment().startOf('day').valueOf()) / 60000);
};

// It Was created to use in the SortableComponent
export const arrayMove = (arr, oldIndex, newIndex) => {
  if (newIndex >= arr.length) {
    let k = newIndex - arr.length + 1;
    // eslint-disable-next-line no-plusplus
    while (k--) {
      arr.push(undefined);
    }
  }
  arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
  return arr;
};

export const camelize = obj =>
  transform(obj, (acc, value, key, target) => {
    const camelKey = isArray(target) ? key : camelCase(key);

    acc[camelKey] = isObject(value) ? camelize(value) : value;
  });

export const hasUsersAssigned = (members = { users: [], groups: [] }) => {
  const { users, groups } = members;
  return users?.length > 0 || groups?.length > 0;
};

export const removeAuditFields = (obj = {}) => {
  const newObj = { ...obj };
  delete newObj?.createdAt;
  delete newObj?.updatedAt;
  delete newObj?.uid;

  return newObj;
};

export const getProjectAccordingToDomain = () => {
  const PROJECTS = {
    operonda: {
      id: 'operonda',
      title: 'Operonda',
      logo: '/logo_operonda.png',
      selected: false,
    },
    guarnic: {
      id: 'guarnic',
      title: 'GUARNIC',
      logo: '/guarnic_144.png',
      selected: false,
    },
  };

  const hostname = window.location.hostname.toLowerCase();
  const domain = hostname.split('.');

  return {
    title: domain.includes('guarnic') ? PROJECTS.guarnic.title : PROJECTS.operonda.title,
    logo: domain.includes('guarnic') ? PROJECTS.guarnic.logo : PROJECTS.operonda.logo,
    project: domain.includes('guarnic') ? PROJECTS.guarnic.id : PROJECTS.operonda.id,
  };
};

export const smartTruncate = (text, maxLength = 20) =>
  text.length > maxLength ? `${text.substring(0, maxLength - 3)}...` : text;

export const formatMinutesToHoursString = minutes => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours === 0) {
    const minutesLabel = translateString(
      remainingMinutes !== 1 ? 'general.minutes' : 'general.minute',
    );
    return `${remainingMinutes} ${minutesLabel}`;
  }

  if (remainingMinutes === 0) {
    const hoursLabel = translateString(hours !== 1 ? 'general.hours' : 'general.hour');
    return `${hours} ${hoursLabel}`;
  }

  const hoursLabel = translateString(hours !== 1 ? 'general.hours' : 'general.hour');
  const minutesLabel = translateString(
    remainingMinutes !== 1 ? 'general.minutes' : 'general.minute',
  );
  const andLabel = translateString('general.and');
  return `${hours} ${hoursLabel} ${andLabel} ${remainingMinutes} ${minutesLabel}`;
};

/**
 * Get device information including platform, OS version, and app version
 * @returns {Object} Device information object
 */
export const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  const appVersion = '-';

  let platform = 'web';
  let osVersion = '';

  if (/Android/i.test(userAgent)) {
    platform = 'android';
    const androidMatch = userAgent.match(/Android\s+([\d.]+)/);
    osVersion = androidMatch ? `Android ${androidMatch[1]}` : 'Android';
  } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
    platform = 'ios';
    const iosMatch = userAgent.match(/OS\s+([\d_]+)/);
    osVersion = iosMatch ? `iOS ${iosMatch[1].replace(/_/g, '.')}` : 'iOS';
  } else if (/Windows/i.test(userAgent)) {
    platform = 'web';
    const windowsMatch = userAgent.match(/Windows NT\s+([\d.]+)/);
    osVersion = windowsMatch ? `Windows ${windowsMatch[1]}` : 'Windows';
  } else if (/Mac OS X/i.test(userAgent)) {
    platform = 'web';
    const macMatch = userAgent.match(/Mac OS X\s+([\d._]+)/);
    osVersion = macMatch ? `macOS ${macMatch[1].replace(/_/g, '.')}` : 'macOS';
  } else if (/Linux/i.test(userAgent)) {
    platform = 'web';
    osVersion = 'Linux';
  }

  const deviceId = `device_${platform}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  return {
    id: deviceId,
    platform,
    app_version: appVersion,
    os_version: osVersion,
  };
};
